import LoopSeriesPanel from "../loop-series-panel";
import { Accordion, Button } from "@heroui/react";
import { CalendarDays, ChevronRight, Clock, MapPin, Mic, Users } from "lucide-react";
import IconAvatar from "@/components/icon-avatar";
import MemberAvatar from "@/components/members/member-avatar";
import ExtraCountAvatar from "@/components/members/extra-count-avatar";
import { formatDate, formatTime12h, getReadableTimeDiff, getRelativeDateTimeLabel } from "@/utils/dates";

/**
 * A single editable detail row. Renders as a button when onPress is given,
 * otherwise as a plain div — so the same row works in the readonly case
 * without a separate component.
 */
const DetailRow = ({ icon: Icon, label, value, sub, onPress }) => {
  const content = (
    <>
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Icon size={16} />
        </div>
        <div className="flex min-w-0 flex-col items-start gap-0.5 text-left">
          <span className="text-xs text-muted">{label}</span>
          <span className="truncate text-sm font-semibold">{value}</span>
          {sub && <span className="truncate text-xs text-muted">{sub}</span>}
        </div>
      </div>

      {onPress && <ChevronRight className="size-4 shrink-0 text-muted" />}
    </>
  );

  if (!onPress) {
    return <div className="flex w-full items-center justify-between gap-3 rounded-xl border p-3">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onPress}
      className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors hover:border-accent hover:bg-accent-soft/40"
    >
      {content}
    </button>
  );
};

const LoopOverviewPanel = ({ record, readonly, onEdit }) => {
  const startTime = record?.start_time?.set;
  const endTime = record?.end_time?.real || record?.end_time?.set;

  const duration = startTime && endTime ? getReadableTimeDiff(startTime, endTime) : null;

  // Optional chaining throughout — location is missing entirely on some
  // records, and "Not Set" is the API's placeholder for an unset one.
  const locationName = record?.location?.name;
  const hasLocation = Boolean(locationName && locationName !== "Not Set");

  const participants = record?.members ?? [];
  const participantCount = participants.length;

  const handleEdit = (section) => (readonly ? undefined : () => onEdit?.(section));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <DetailRow
          icon={CalendarDays}
          label="Date"
          value={record?.meeting_date ? formatDate(record.meeting_date) : "No date set"}
          onPress={handleEdit("date")}
        />

        <DetailRow
          icon={Clock}
          label="Time"
          value={
            startTime && endTime ? `${formatTime12h(startTime)} – ${formatTime12h(endTime)}` : "No time set"
          }
          sub={duration}
          onPress={handleEdit("date")}
        />

        <DetailRow
          icon={MapPin}
          label="Location"
          value={hasLocation ? locationName : "No location added"}
          onPress={handleEdit("location")}
        />

        <DetailRow
          icon={Users}
          label="Participants"
          value={`${participantCount} ${participantCount === 1 ? "person" : "people"}`}
          sub={
            participantCount > 0 ? (
              <div className="flex -space-x-1 mt-1">
                {participants.slice(0, 3).map((m, idx) => (
                  <MemberAvatar
                    key={m.member_email || m.member_id || idx}
                    member={{
                      email: m.member_email,
                      fullname: m.member_fullname,
                      id: m.member_id,
                    }}
                    size="xs"
                    className="ring-2 ring-white dark:ring-surface"
                  />
                ))}
                {participantCount > 3 && (
                  <ExtraCountAvatar
                    count={participantCount - 3}
                    members={participants.slice(3).map((m) => ({
                      email: m.member_email,
                      fullname: m.member_fullname,
                      id: m.member_id,
                    }))}
                    size="size-4"
                  />
                )}
              </div>
            ) : null
          }
          onPress={handleEdit("participants")}
        />
      </div>

      <Accordion variant="surface" className="shadow-none border rounded-xl" defaultExpandedKeys={["about"]}>
        <Accordion.Item key={"about"} id={"about"}>
          <Accordion.Heading>
            <Accordion.Trigger className="flex items-center justify-between w-full px-4 py-3 text-left rounded-xl">
              <span className="font-semibold">About this loop</span>
              <Accordion.Indicator />
            </Accordion.Trigger>
          </Accordion.Heading>

          <Accordion.Panel>
            <Accordion.Body className="px-4 pb-4 text-sm text-muted">
              {record?.description?.trim() || "No description provided for this loop yet."}
            </Accordion.Body>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <LoopSeriesPanel record={record} mode="overview" />

      <Button variant="outline" onPress={() => window.location.assign(`/recordings?selected=${encodeURIComponent(record._id)}`)}>
        Open recording and transcript
      </Button>

    </div>
  );
};

export default LoopOverviewPanel;